import { keepPreviousData } from '@tanstack/react-query';
import { BROWSE_MIN_QUERY_LENGTH, BROWSE_PAGE_SIZE_TYPE } from 'fym-shared/src/music24/browseSearch';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';
import type { BrowseUrlState } from './useBrowseUrlState';

/** True when the query is long enough to search (design: 3+ characters). */
export function isSearchableQuery(query: string): boolean {
  return query.trim().length >= BROWSE_MIN_QUERY_LENGTH;
}

/**
 * The server search (`browse.search`). The previous answer stays on screen while the next one
 * loads (design b3: never swap the results for a spinner on each keystroke). `limit` grows with
 * "Show more" on one type.
 */
export function useBrowseSearch(state: BrowseUrlState, limit: number = BROWSE_PAGE_SIZE_TYPE) {
  const trpc = useTRPCAPI();
  const query = state.query.trim();
  return useTRPCQuery(
    trpc.browse.search.queryOptions(
      {
        q: query,
        type: state.type,
        sort: state.sort,
        genre: state.genre,
        countryCode: state.countryCode,
        service: state.service,
        hideFollowed: state.hideFollowed,
        offset: 0,
        limit,
      },
      {
        enabled: isSearchableQuery(query),
        placeholderData: keepPreviousData,
        staleTime: 60 * 1000,
        // A rate limit or a refused filter does not get better on a retry.
        retry: 1,
      }
    )
  );
}

export type BrowseSearchData = NonNullable<ReturnType<typeof useBrowseSearch>['data']>;
export type BrowseArtistItem = NonNullable<BrowseSearchData['artists']>['items'][number];
export type BrowseTrackItem = NonNullable<BrowseSearchData['tracks']>['items'][number];
export type BrowsePlaylistItem = NonNullable<BrowseSearchData['playlists']>['items'][number];
export type BrowseTop = NonNullable<BrowseSearchData['top']>;
